import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Cross2Icon } from '@radix-ui/react-icons';
import { useIsMobile } from '@/shared/hooks/use-is-mobile';
import { useAuth } from '@context/authentication';
import { VisitsService } from '@services/requests/housien-services/VisitsService';
import { VisitDrawer } from '@features/visits/components/VisitDrawer';
import { CardListSkeleton } from '@/shared/ui/components/basic/data-state/skeletons';
import { DataState, DataStateError, UnstyledButton } from '@/shared/ui';
import type { Visit } from '@services/requests/housien-models/Visit';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/ui/components/shadcn/dialog';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/shared/ui/components/shadcn/sheet';
import { VisitDetailHeroCard } from './VisitDetailHeroCard';
import { VisitDetailActions } from './VisitDetailActions';
import { VisitDetailBody } from './VisitDetailBody';

interface VisitDetailOverlayProps {
  visitId: string | null;
  agencyId: string;
  members: unknown[];
  onClose: () => void;
  onVisitUpdated?: () => void;
}

/**
 * Maps a Visit into the generic record shape expected by the edit drawer.
 */
function normalizeVisitForDrawer(visit: Visit): Record<string, unknown> {
  return {
    ...visit,
    broker_email: visit.owner_email ?? (visit.broker_emails?.[0] ?? ''),
  };
}

/**
 * Responsive visit detail overlay: Dialog on desktop, Sheet on mobile.
 * Fetches visit data by ID and displays hero, actions, and body cards.
 */
export function VisitDetailOverlay({
  visitId,
  agencyId,
  members,
  onClose,
  onVisitUpdated,
}: VisitDetailOverlayProps) {
  const { t } = useTranslation('@pages/visits');
  const isMobile = useIsMobile();
  const open = Boolean(visitId);

  const handleOpenChange = (value: boolean) => {
    if (!value) onClose();
  };

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent side="bottom" className="h-[92vh] overflow-y-auto p-0" hideClose>
          <SheetTitle className="sr-only">{t('kanban.detail.title')}</SheetTitle>
          {visitId && (
            <OverlayContent
              visitId={visitId}
              agencyId={agencyId}
              members={members}
              onClose={onClose}
              onVisitUpdated={onVisitUpdated}
            />
          )}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[640px] max-h-[85vh] overflow-y-auto p-0">
        <DialogTitle className="sr-only">{t('kanban.detail.title')}</DialogTitle>
        {visitId && (
          <OverlayContent
            visitId={visitId}
            agencyId={agencyId}
            members={members}
            onClose={onClose}
            onVisitUpdated={onVisitUpdated}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface OverlayContentProps {
  visitId: string;
  agencyId: string;
  members: unknown[];
  onClose: () => void;
  onVisitUpdated?: () => void;
}

/**
 * Inner content of the visit detail overlay managing data fetching and layout.
 */
function OverlayContent({
  visitId,
  agencyId,
  members,
  onClose,
  onVisitUpdated,
}: OverlayContentProps) {
  const { t } = useTranslation('@pages/visits');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data: visit, isLoading, isError } = useQuery({
    queryKey: ['visit-detail', agencyId, visitId],
    queryFn: () => VisitsService.getVisitDetails(agencyId, visitId),
    enabled: Boolean(agencyId && visitId),
  });

  const handleEdit = useCallback(() => setDrawerOpen(true), []);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);
  const handleDrawerSuccess = useCallback(() => {
    setDrawerOpen(false);
    queryClient.invalidateQueries({ queryKey: ['visit-detail', agencyId, visitId] });
    onVisitUpdated?.();
  }, [queryClient, agencyId, visitId, onVisitUpdated]);

  return (
    <div className="flex flex-col p-[var(--space-5)] gap-[var(--space-3)]">
      <UnstyledButton
        type="button"
        onClick={onClose}
        className="absolute right-[var(--space-4)] top-[var(--space-4)] rounded-(--radius-sm) opacity-70 hover:opacity-100 transition-opacity cursor-pointer z-10"
        aria-label={t('common.close', 'Fechar')}
      >
        <Cross2Icon className="h-4 w-4" />
      </UnstyledButton>

      <DataState
        isLoading={isLoading}
        isError={isError}
        isEmpty={!visit}
        loadingFallback={<CardListSkeleton count={3} />}
        errorState={
          <DataStateError
            message={t('visits.kanban.loadError', 'Erro ao carregar visita')}
          />
        }
        emptyState={
          <div className="py-(--space-10) text-center text-[var(--text-tertiary)]">
            {t('visits.detail.notFound', 'Visita não encontrada')}
          </div>
        }
      >
        {visit && (
          <>
            <VisitDetailHeroCard visit={visit} agencyId={agencyId} />
            <VisitDetailActions
              visit={visit}
              agencyId={agencyId}
              onEdit={handleEdit}
              onUpdated={onVisitUpdated}
            />
            <VisitDetailBody visit={visit} agencyId={agencyId} />
            <VisitDrawer
              open={drawerOpen}
              onClose={handleDrawerClose}
              onSuccess={handleDrawerSuccess}
              agencyId={agencyId}
              userEmail={user?.profile?.email ?? ''}
              editingVisit={normalizeVisitForDrawer(visit)}
              members={members as any[]}
            />
          </>
        )}
      </DataState>
    </div>
  );
}
